import {
  ConfirmNotification,
  DataGridActionsColumn,
  TableView1,
} from "@/components";
import React, { useContext, useMemo, useState } from "react";
import { useDeleteRole, usePageTitle, useRolesOnlyList } from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { Button, Stack } from "@mui/material";
import { bool, string } from "prop-types";
import { useNavigate } from "react-router";
import { AddRolePage } from "@/pages";

interface RoleRow {
  id: number;
  key?: string;
  role?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface RolesListPageProps {
  showAdd?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showPermissions?: boolean;
  parentPath?: string;
}

export const RolesListPage = (props: RolesListPageProps) => {
  const navigate = useNavigate();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { deleteRole, deleting } = useDeleteRole();

  // Set page title
  usePageTitle(gettext("Roles"));

  const { rolesList, loading } = useRolesOnlyList();

  //#region React Hook (useState)
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [dialogParameters, setDialogParameters] = useState<any>({});
  const [addFormOpen, setAddFormOpen] = useState<boolean>(false);
  const [editRow, setEditRow] = useState<RoleRow | {}>({});
  //#endregion React Hook (useState)

  // Use props directly instead of state to avoid cascading renders
  const { showAdd, showEdit, showDelete, showPermissions, parentPath } = props;

  //#region useMemo

  const showItems = useMemo(() => {
    return {
      showEdit: showEdit ?? false,
      showDelete: showDelete ?? false,
      showPermissions: showPermissions ?? false,
    };
  }, [showEdit, showDelete, showPermissions]);

  const showActionsColumn = useMemo(() => {
    return Object.values(showItems).some((item) => item === true);
  }, [showItems]);

  //#endregion useMemo

  //#region Handlers

  const menuClick = (action: string, row: Record<string, any>) => {
    const roleRow = row as RoleRow;

    switch (action) {
      case "role-edit":
        handleEdit(roleRow);
        break;
      case "role-delete":
        handleOnClickDeleteSingle(roleRow);
        break;
      case "role-permissions":
        handleOnClickPermission(roleRow);
        break;
      default:
        break;
    }
  };

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        role: true,
        key: true,
        createdAt: false,
        updatedAt: false,
      },
    },
  };
  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
    },
    {
      field: "role",
      headerName: gettext("Role"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "key",
      headerName: gettext("Key"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "createdAt",
      headerName: gettext("Created At"),
      headerAlign: "left",
      type: "date",
      valueGetter: (params: { value?: string | number | Date }) =>
        params.value && new Date(params.value),
    },
    {
      field: "updatedAt",
      headerName: gettext("Updated At"),
      headerAlign: "left",
      type: "date",
      valueGetter: (params: { value?: string | number | Date }) =>
        params.value && new Date(params.value),
    },
    ...DataGridActionsColumn(showActionsColumn, "role", showItems, menuClick),
  ];

  //region Handlers (continued)
  const handleEdit = (row: RoleRow) => {
    setEditRow(row);
    setAddFormOpen(true);
  };

  const handleOnClickDeleteSingle = (row: RoleRow) => {
    setDialogParameters({
      action: "delete-single",
      key: row.key,
    });
    setShowConfirm(true);
  };

  const handleOnClickDeleteMultiple = (ids: Array<string | number>) => {
    setDialogParameters({
      action: "delete-multi",
      ids: ids,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: { result?: string; parameters?: any }) => {
    if (obj.result === "yes") {
      deleteRole({
        key: obj?.parameters?.key,
      });
    }
    setShowConfirm(false);
  };

  const handleOnClickPermission = (row: RoleRow) => {
    const key = row.key;
    navigate(`/${parentPath}/roles/permissions/${key}`);
  };

  const postToolbar = () => {
    return (
      <>
        {showAdd && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ pl: 1, alignItems: "center" }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setAddFormOpen(true);
              }}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {gettext("Add role")}
            </Button>
          </Stack>
        )}
      </>
    );
  };

  const handleCloseAddForm = (event: boolean) => {
    setAddFormOpen(event);
    setEditRow({});
  };
  //endregion Handlers

  return (
    <>
      <TableView1
        title={gettext("Roles")}
        rows={rolesList}
        // rows={[]}
        columns={columns}
        loading={loading}
        checkboxSelection={false}
        initialState={initialState}
        // preToolbar={preToolbar()}
        postToolbar={postToolbar()}
        // headerRight={headerRight()}
        showGridToolbar={true}
        showGridToolbarDeleteButton={false}
        showGridToolbarDensitySelector={false}
        showGridToolbarColumnsButton={false}
        showGridToolbarQuickFilter
        showGridToolbarFilterButton={false}
        showGridToolbarExport={false}
        // onRowSelectionModelChange={handleOnRowSelectionModelChange}
        onClickDeleteMultiple={handleOnClickDeleteMultiple}
      />

      <AddRolePage
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
      />
      {deleting ? (
        <div className="position-absolute bg-dark opacity-50 w-100 h-100 d-flex justify-content-center align-items-center">
          {/*<Spinner animation="border" variant="light" />*/}
        </div>
      ) : null}
    </>
  );
};

RolesListPage.propTypes = {
  showAdd: bool,
  showEdit: bool,
  showDelete: bool,
  showPermissions: bool,
  parentPath: string,
};
